import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Initiative, InitiativeType, InitiativePriority } from './types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<Initiative>) => Promise<void>;
}

export const InitiativeCreateModal: React.FC<Props> = ({ isOpen, onClose, onSubmit }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [businessCase, setBusinessCase] = useState('');
  const [initiativeType, setInitiativeType] = useState<InitiativeType>('STRATEGIC');
  const [priority, setPriority] = useState<InitiativePriority>('MEDIUM');
  const [targetDepartment, setTargetDepartment] = useState('Flight Operations');
  const [targetDomain, setTargetDomain] = useState('FLIGHT_OPS');
  const [plannedStartDate, setPlannedStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [plannedEndDate, setPlannedEndDate] = useState(new Date(Date.now() + 180 * 86400000).toISOString().split('T')[0]);
  const [estimatedBudget, setEstimatedBudget] = useState(50000);
  const [sponsorName, setSponsorName] = useState('Cemil Demir (Accountable Manager)');
  const [ownerName, setOwnerName] = useState('Captain Burak Demir');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Initiative title is required.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await onSubmit({
        title,
        description,
        business_case: businessCase,
        initiative_type: initiativeType,
        priority,
        target_department: targetDepartment,
        target_domain: targetDomain,
        planned_start_date: plannedStartDate,
        planned_end_date: plannedEndDate,
        estimated_budget: Number(estimatedBudget),
        sponsor_name: sponsorName,
        owner_name: ownerName
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create initiative');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card max-w-2xl">
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>Launch Strategic Safety Initiative</h3>
            <p>Define enterprise objectives, deliverables, and resource allocation</p>
          </div>
          <button type="button" onClick={onClose} className="modal-close-btn" aria-label="Close modal">
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="modal-body space-y-4">
            <div className="form-field">
              <label>Initiative Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Paperless Digital Flight Deck & Operational Excellence"
                required
              />
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Initiative Type</label>
                <select
                  value={initiativeType}
                  onChange={(e) => setInitiativeType(e.target.value as InitiativeType)}
                >
                  <option value="STRATEGIC">Strategic Initiative</option>
                  <option value="SAFETY_IMPROVEMENT">Safety Improvement</option>
                  <option value="QUALITY_EXCELLENCE">Quality Excellence</option>
                  <option value="OPERATIONAL_EFFICIENCY">Operational Efficiency</option>
                  <option value="COMPLIANCE_MANDATE">Compliance Mandate</option>
                  <option value="DIGITAL_TRANSFORMATION">Digital Transformation</option>
                </select>
              </div>

              <div className="form-field">
                <label>Priority</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as InitiativePriority)}
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="CRITICAL">Critical</option>
                </select>
              </div>
            </div>

            <div className="form-field">
              <label>Description & Scope of Work</label>
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Core objectives, operational scope, and major deliverables..."
              />
            </div>

            <div className="form-field">
              <label>Business Case & Safety Rationale</label>
              <textarea
                rows={2}
                value={businessCase}
                onChange={(e) => setBusinessCase(e.target.value)}
                placeholder="Return on investment, risk reduction factors, or regulatory requirements..."
              />
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Target Department</label>
                <input
                  type="text"
                  value={targetDepartment}
                  onChange={(e) => setTargetDepartment(e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Estimated Budget ($)</label>
                <input
                  type="number"
                  value={estimatedBudget}
                  onChange={(e) => setEstimatedBudget(Number(e.target.value))}
                />
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Planned Start Date</label>
                <input
                  type="date"
                  value={plannedStartDate}
                  onChange={(e) => setPlannedStartDate(e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Target Completion Date</label>
                <input
                  type="date"
                  value={plannedEndDate}
                  onChange={(e) => setPlannedEndDate(e.target.value)}
                />
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Initiative Owner</label>
                <input
                  type="text"
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Executive Sponsor</label>
                <input
                  type="text"
                  value={sponsorName}
                  onChange={(e) => setSponsorName(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Creating...' : 'Create Initiative'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
